/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// =========================================
// IndexedDB Manager per DOS518
// Gestione locale di foto/video/media
// =========================================

const DB_NAME = 'DOS518_MediaDB';
const DB_VERSION = 1;
const STORE_NAME = 'media';

export interface MediaBlob {
  id: string;
  blob: Blob;
  type: string;
  timestamp: string;
}

class IndexedDBManager {
  private db: IDBDatabase | null = null;

  // Inizializza il database
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ Errore apertura IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB inizializzato');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Crea object store se non esiste
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('type', 'type', { unique: false });
          console.log('✅ Object store creato');
        }
      };
    });
  }

  // Salva un file (foto/video) come Blob
  async saveMedia(id: string, file: File): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const mediaBlob: MediaBlob = {
        id,
        blob: file,
        type: file.type,
        timestamp: new Date().toISOString()
      };

      const request = store.put(mediaBlob);

      request.onsuccess = () => {
        console.log(`✅ Media salvato: ${id}`);
        resolve();
      };

      request.onerror = () => {
        console.error(`❌ Errore salvataggio media ${id}:`, request.error);
        reject(request.error);
      };
    });
  }

  // Recupera un file dal database
  async getMedia(id: string): Promise<Blob | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => {
        const result = request.result as MediaBlob | undefined;
        resolve(result ? result.blob : null);
      };

      request.onerror = () => {
        console.error(`❌ Errore recupero media ${id}:`, request.error);
        reject(request.error);
      };
    });
  }

  // Recupera tutti i media
  async getAllMedia(): Promise<MediaBlob[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        console.error('❌ Errore recupero tutti i media:', request.error);
        reject(request.error);
      };
    });
  }

  // Elimina un media
  async deleteMedia(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log(`✅ Media eliminato: ${id}`);
        resolve();
      };

      request.onerror = () => {
        console.error(`❌ Errore eliminazione media ${id}:`, request.error);
        reject(request.error);
      };
    });
  }

  // Svuota tutti i media (reset)
  async clearAllMedia(): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('✅ Tutti i media eliminati');
        resolve();
      };

      request.onerror = () => {
        console.error('❌ Errore pulizia database:', request.error);
        reject(request.error);
      };
    });
  }

  // Ottieni statistiche storage
  async getStorageStats(): Promise<{ count: number; estimatedSize: string }> {
    if (!this.db) await this.init();

    const allMedia = await this.getAllMedia();
    const count = allMedia.length;
    
    // Stima dimensione (approssimativa)
    let totalSize = 0;
    allMedia.forEach(media => {
      totalSize += media.blob.size;
    });

    const estimatedSize = this.formatBytes(totalSize);

    return { count, estimatedSize };
  }

  // Formatta bytes in formato leggibile
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  // Converti Blob in Base64 (per export)
  async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Rimuovi il prefix "data:image/jpeg;base64," per avere solo la stringa base64
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Converti Base64 in Blob (per import)
  base64ToBlob(base64: string, type: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type });
  }
}

// Istanza singleton
export const indexedDBManager = new IndexedDBManager();

// Inizializza automaticamente quando il modulo viene importato
indexedDBManager.init().catch(err => {
  console.error('❌ Errore inizializzazione IndexedDB:', err);
});

export default indexedDBManager;
